import { mdiCash } from '@mdi/js';
import { Button } from '@mollybet/ui';
import { Currency } from 'common/classes/currency';
import {
  BaseTable,
  ColumnProp,
  TotalCell,
  TotalRow,
  Loading,
} from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { CurrencyCodes } from 'common/configs/currency';
import { Xrates } from 'common/state/accounts/actions';
import { CustomerSummary } from 'common/state/customers/actions';
import React, { useMemo } from 'react';
import { ModalModes } from '../customer';

export interface StatementsTableProps {
  summaries: CustomerSummary[];
  xrates: Xrates;
  setClientCode: (code: string) => void;
  setModalMode: (mode: ModalModes) => void;
  isLoading?: boolean;
}

export const SubRowGenerator =
  (xrates: Xrates, summaries: CustomerSummary[]) =>
  ({ index, style }) => {
    if (!summaries || index !== summaries.length - 1) return null;

    const totalBalance = summaries.reduce(
      (acc, summary) =>
        acc +
        parseFloat(summary.balances.final_balance) /
          xrates[summary.wallet_currency],
      0
    );

    return (
      <TotalRow role="row" style={style}>
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell />
        <TotalCell>Total</TotalCell>
        <TotalCell role="cell">
          {new Currency(totalBalance, CurrencyCodes.GBP).toElement()}
        </TotalCell>
      </TotalRow>
    );
  };

export const StatementsTable = ({
  summaries,
  xrates,
  setClientCode,
  setModalMode,
  isLoading,
}: StatementsTableProps) => {
  const columns = useMemo(
    (): ColumnProp<CustomerSummary>[] => [
      { key: 'wallet_currency', title: 'Currency' },
      {
        key: 'upline_settled',
        title: 'PL Settled',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.cleared, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.cleared, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared),
      },
      {
        key: 'reconcilled',
        title: 'Reconcilled Under Settlement Threshold',
        width: 180,
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            row.balances.reconciled,
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.reconciled, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.reconciled),
      },
      {
        key: 'pending',
        title: 'Pending To Reconcile Prior Weeks',
        width: 180,
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.pending, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.pending, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.pending),
      },
      {
        key: 'outstanding',
        title: 'Current Week Outstanding (Cached)',
        width: 120,
        valueFormatter: (row: CustomerSummary) => (
          row.balances.outstanding !== null
            ? <div title={`Cached at ${row.balances.outstanding_age}`}>
                {new Currency(
                  row.balances.outstanding,
                  row.wallet_currency
                ).toElement()}
              </div>
            : <Loading small>Loading...</Loading>
        ),
        csvFormatter: (row) =>
          new Currency(row.balances.outstanding, row.wallet_currency).toString({
            symbol: false,
          }),
      },
      {
        key: 'total',
        title: 'Total PL',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding),
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding),
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared) +
          parseFloat(row.balances.reconciled) +
          parseFloat(row.balances.pending) +
          parseFloat(row.balances.outstanding),
      },
      {
        key: 'deposits',
        title: 'Deposits / Withdrawals',
        width: 110,
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.deposits, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.deposits, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.deposits),
      },
      {
        key: 'ajustments',
        title: 'Adjustments',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            row.balances.adjustments,
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.adjustments, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.adjustments),
      },
      {
        key: 'final_balance',
        title: 'Client Account Balance per Currency',
        width: 190,
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.adjustments) +
              parseFloat(row.balances.deposits),
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.adjustments) +
              parseFloat(row.balances.deposits),
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared) +
          parseFloat(row.balances.reconciled) +
          parseFloat(row.balances.pending) +
          parseFloat(row.balances.outstanding) +
          parseFloat(row.balances.adjustments) +
          parseFloat(row.balances.deposits),
      },
      {
        key: 'gbp_balance',
        title: 'Client Account Balance (GBP)',
        width: 130,
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            (parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.adjustments) +
              parseFloat(row.balances.deposits)) /
              xrates[row.wallet_currency],
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(
            (parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.adjustments) +
              parseFloat(row.balances.deposits)) /
              xrates[row.wallet_currency],
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row: CustomerSummary) =>
          (parseFloat(row.balances.cleared) +
            parseFloat(row.balances.reconciled) +
            parseFloat(row.balances.pending) +
            parseFloat(row.balances.outstanding) +
            parseFloat(row.balances.adjustments) +
            parseFloat(row.balances.deposits)) /
          xrates[row.wallet_currency],
      },
      {
        key: 'actions',
        title: 'Actions',
        valueFormatter: (row: CustomerSummary) => (
          <Button
            onClick={() => {
              setClientCode(row.client_account_code);
              setModalMode('adjustment');
            }}
          >
            Adjust
          </Button>
        ),
      },
    ],
    [xrates, setClientCode, setModalMode]
  );

  const csvExtra = useMemo(() => {
    if (!summaries) return null;

    const totalBalance = summaries.reduce(
      (acc, summary) =>
        acc +
        parseFloat(summary.balances.final_balance) /
          xrates[summary.wallet_currency],
      0
    );

    return `,,,,,,,,,"${new Currency(totalBalance, CurrencyCodes.GBP).toString({
      symbol: 'code',
    })}"`;
  }, [summaries, xrates]);

  return (
    <>
      <TableTitle icon={mdiCash} title="Statements" />
      <BaseTable
        columns={columns}
        rows={summaries || []}
        csvName="Statements"
        csvExtra={csvExtra}
        paginate={false}
        isLoading={isLoading}
        SubRow={SubRowGenerator(xrates, summaries)}
      />
    </>
  );
};
